import React, { useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Download, FileSpreadsheet, FileText, Printer } from 'lucide-react';
import { useEmpExport } from '@/hooks/useEmpExport';
import { useGerarRelatorioPdf } from '@/hooks/useGerarRelatorioPdf';
import type { RelatorioFinanciadorRow } from '@/hooks/useEmpRelatorioFinanciador';
import { toast } from 'sonner';

interface Props {
  contratoId: string;
  contratoNome: string;
  data: RelatorioFinanciadorRow | null;
}

const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString('pt-BR') : '-';
const fmtNum = (n: number | null, dec = 2) => n != null ? n.toFixed(dec) : '-';

function buildExportRows(d: RelatorioFinanciadorRow): Record<string, unknown>[] {
  const drivers = Array.isArray(d.drivers_risco) ? d.drivers_risco.map(dr => dr.fator).join('; ') : '-';
  return [{
    'Contrato': d.contrato_nome,
    'Contratada': d.contratada ?? '-',
    'Status': d.contrato_status ?? '-',
    'Início': fmtDate(d.data_inicio),
    'Prazo Previsto': fmtDate(d.data_fim_prevista),
    'Progresso Físico (%)': fmtNum(d.progresso_fisico_pct, 1),
    'SPI': fmtNum(d.spi),
    'CPI': fmtNum(d.cpi),
    'Tendência SPI': d.trend_spi ?? '-',
    'Prob. Cumprimento (%)': fmtNum(d.prob_on_time, 0),
    'P10 (data)': fmtDate(d.p10_data),
    'P50 (data)': fmtDate(d.p50_data),
    'P80 (data)': fmtDate(d.p80_data),
    'P90 (data)': fmtDate(d.p90_data),
    'P10 (dias)': d.p10_dias ?? '-',
    'P50 (dias)': d.p50_dias ?? '-',
    'P80 (dias)': d.p80_dias ?? '-',
    'P90 (dias)': d.p90_dias ?? '-',
    'Média MC (dias)': fmtNum(d.mc_media_dias, 0),
    'Desvio MC (dias)': fmtNum(d.mc_desvio_dias, 0),
    'Forecast Término': fmtDate(d.forecast_termino),
    'EAC': d.eac ?? '-',
    'BAC': d.bac ?? '-',
    'Drivers de Risco': drivers,
    'Alertas Ativos': d.alertas_ativos_count ?? 0,
    'Alertas Críticos': d.alertas_criticos_count ?? 0,
    'Data Cálculo MC': fmtDate(d.mc_data_calculo),
  }];
}

export const FinancierExportButton: React.FC<Props> = ({ contratoId, contratoNome, data }) => {
  const { exportToExcel } = useEmpExport();
  const { loading: pdfLoading } = useGerarRelatorioPdf();

  const handleExcel = useCallback(() => {
    if (!data) { toast.error('Sem dados para exportar'); return; }
    exportToExcel(buildExportRows(data), `relatorio-financiador-${contratoNome}`);
  }, [data, contratoNome, exportToExcel]);

  const handleCSV = useCallback(() => {
    if (!data) { toast.error('Sem dados para exportar'); return; }
    const rows = buildExportRows(data);
    const headers = Object.keys(rows[0]);
    const csv = [
      headers.join(';'),
      ...rows.map(r => headers.map(h => `"${String(r[h] ?? '').replace(/"/g, '""')}"`).join(';')),
    ].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `relatorio-financiador-${contratoNome}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exportado');
  }, [data, contratoNome]);

  const handlePDF = useCallback(async () => {
    try {
      const { data: session } = await (await import('@/integrations/supabase/client')).supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) { toast.error('Sessão expirada'); return; }

      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const url = `https://${projectId}.supabase.co/functions/v1/gerar-relatorio-pdf`;
      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ tipo: 'relatorio_financiador', contrato_id: contratoId }),
      });
      if (!resp.ok) {
        const err = await resp.text();
        throw new Error(err);
      }
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `relatorio-financiador-${contratoNome}.pdf`;
      a.click();
      URL.revokeObjectURL(blobUrl);
      toast.success('PDF gerado com sucesso');
    } catch (err: any) {
      toast.error(err.message || 'Erro ao gerar PDF');
    }
  }, [contratoId, contratoNome]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={!data}>
          <Download className="h-4 w-4 mr-1" /> Relatório Financiador
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={handleExcel}>
          <FileSpreadsheet className="h-4 w-4 mr-2" /> Excel (.xlsx)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleCSV}>
          <FileText className="h-4 w-4 mr-2" /> CSV
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handlePDF}>
          <Printer className="h-4 w-4 mr-2" /> PDF
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
